// @spec SHL-STR-019

import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';

const STORAGE_KEY_PREFIX = 'quantifier-search-state';

/**
 * Search navigation state preserved when navigating to a result.
 */
export interface SearchNavigationState {
  /** The search query at time of navigation */
  query: string;
  /** Scroll position of search results list */
  scrollPosition: number;
  /** Unix timestamp when state was saved */
  timestamp: number;
}

/**
 * Options for navigating to a search result.
 */
export interface NavigateToResultOptions {
  /** Open in new browser tab */
  newTab?: boolean;
  /** Term to highlight on destination page */
  highlightTerm?: string;
}

/**
 * Return type for the useSearchNavigation hook.
 */
export interface UseSearchNavigationReturn {
  /** Navigate to a search result with state preservation */
  navigateToResult: (url: string, options?: NavigateToResultOptions) => void;
  /** Restore search state if returning from a result page */
  restoreSearchState: () => SearchNavigationState | null;
  /** Clear stored search state */
  clearSearchState: () => void;
  /** Check if there's a search state to restore */
  hasSearchStateParam: boolean;
}

/**
 * Generate a unique ID for search state storage.
 */
function generateStateId(): string {
  return `${String(Date.now())}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Get storage key for a state ID.
 */
function getStorageKey(stateId: string): string {
  return `${STORAGE_KEY_PREFIX}:${stateId}`;
}

/**
 * Check if sessionStorage is available.
 */
function isStorageAvailable(): boolean {
  try {
    const testKey = '__storage_test__';
    sessionStorage.setItem(testKey, testKey);
    sessionStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Save search state to session storage.
 */
function saveSearchState(stateId: string, state: SearchNavigationState): void {
  if (!isStorageAvailable()) return;

  try {
    sessionStorage.setItem(getStorageKey(stateId), JSON.stringify(state));
  } catch {
    // Silently ignore storage errors
  }
}

/**
 * Load search state from session storage.
 */
function loadSearchState(stateId: string): SearchNavigationState | null {
  if (!isStorageAvailable()) return null;

  try {
    const stored = sessionStorage.getItem(getStorageKey(stateId));
    if (!stored) return null;

    const parsed: unknown = JSON.parse(stored);

    // Validate structure
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !('query' in parsed) ||
      typeof parsed.query !== 'string' ||
      !('scrollPosition' in parsed) ||
      typeof parsed.scrollPosition !== 'number' ||
      !('timestamp' in parsed) ||
      typeof parsed.timestamp !== 'number'
    ) {
      sessionStorage.removeItem(getStorageKey(stateId));
      return null;
    }

    // State is valid for 5 minutes
    const age = Date.now() - parsed.timestamp;
    if (age > 5 * 60 * 1000) {
      sessionStorage.removeItem(getStorageKey(stateId));
      return null;
    }

    return parsed as SearchNavigationState;
  } catch {
    return null;
  }
}

/**
 * Remove search state from session storage.
 */
function removeSearchState(stateId: string): void {
  if (!isStorageAvailable()) return;

  try {
    sessionStorage.removeItem(getStorageKey(stateId));
  } catch {
    // Silently ignore errors
  }
}

/**
 * Hook for search result navigation with state preservation.
 *
 * Features:
 * - Saves search query and scroll position before navigation
 * - Supports opening results in new tabs
 * - Adds highlight parameter for destination pages
 * - Restores search state when navigating back
 *
 * @param currentQuery - The current search query to save
 * @param scrollPosition - Optional scroll position of results list
 *
 * @example
 * function GlobalSearch() {
 *   const { navigateToResult, restoreSearchState } = useSearchNavigation('my query');
 *
 *   // Navigate normally
 *   navigateToResult('/settings/team');
 *
 *   // Open in new tab with highlighting
 *   navigateToResult('/settings/team', {
 *     newTab: true,
 *     highlightTerm: 'team'
 *   });
 * }
 */
export function useSearchNavigation(
  currentQuery: string,
  scrollPosition = 0
): UseSearchNavigationReturn {
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false });
  const searchState = (searchParams as { searchState?: string }).searchState;

  const navigateToResult = useCallback(
    (url: string, options?: NavigateToResultOptions) => {
      const { newTab = false, highlightTerm } = options ?? {};

      // Save current search state
      const stateId = generateStateId();
      saveSearchState(stateId, {
        query: currentQuery,
        scrollPosition,
        timestamp: Date.now(),
      });

      // Build URL with params
      const urlObj = new URL(url, window.location.origin);
      urlObj.searchParams.set('searchState', stateId);
      if (highlightTerm) {
        urlObj.searchParams.set('highlight', highlightTerm);
      }
      const fullUrl = urlObj.pathname + urlObj.search;

      if (newTab) {
        window.open(fullUrl, '_blank');
      } else {
        void navigate({ to: fullUrl });
      }
    },
    [currentQuery, scrollPosition, navigate]
  );

  const restoreSearchState = useCallback((): SearchNavigationState | null => {
    if (!searchState) return null;
    return loadSearchState(searchState);
  }, [searchState]);

  const clearSearchState = useCallback(() => {
    if (searchState) {
      removeSearchState(searchState);
    }
  }, [searchState]);

  return {
    navigateToResult,
    restoreSearchState,
    clearSearchState,
    hasSearchStateParam: Boolean(searchState),
  };
}
